"use client";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import Filters from "./Filters";
import { useSearchParams } from "next/navigation";
import { Badge } from "@/components/ui/badge";
import { LibraryBig } from "lucide-react";
import CustomBreadcrumbs from "@/custom/CustomBreadcrumbs";
import { useContext, useEffect, useState } from "react";
import { CartContext } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { ShoppingCart } from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import { useAcademicSetStore } from "@/store/AcademicSet";
import AcademicProductCard from "@/app/views/AcademicProductCard/components/AcademicProductCard";
import { formatCurrency } from "@/utils/formatCurrency";
import { Checkbox } from "@/components/ui/checkbox";

const Products = ({
  academicProducts,
  stationaryProducts,
  totalAcademic,
  totalStationary,
  schoolClasses,
  range,
}) => {
  const searchParams = useSearchParams();
  const school = searchParams.get("school");
  const className = searchParams.get("class");
  const { setAddToCart } = useContext(CartContext);

  const {
    setSchoolSet,
    removeSchoolSet,
    schoolSet,
    isAllProductSelect,
    setAllProductSelect,
    totalPrice,
    totalSaveAmount,
  } = useAcademicSetStore();

  useEffect(() => {
    setSchoolSet([...academicProducts, ...stationaryProducts]);
    setAllProductSelect(true);
  }, [academicProducts, stationaryProducts]);

  return (
    <div className="my-10">
      <div className="space-y-10">
        <CustomBreadcrumbs text={school} />
        <div className="flex flex-col lg:flex-row items-start gap-4">
          <Filters schoolClasses={schoolClasses} />
          <div className="space-y-3">
            <Card className="flex flex-col gap-4 w-full bg-none">
              <CardHeader className="sticky top-16 md:top-20 z-50 bg-white border-t border-b">
                <div className="flex flex-col sm:flex-row justify-between items-center w-full">
                  <div className="flex flex-wrap h-fit items-center gap-2 text-sm">
                    <p className="text-base text-custom-black  font-medium ">
                      {range}
                    </p>
                    <Badge
                      variant="outline"
                      className="capitalize bg-custom-slider-bg-gray"
                    >
                      search: {school}
                    </Badge>

                    {className && (
                      <Badge
                        variant="outline"
                        className="capitalize bg-custom-slider-bg-gray"
                      >
                        Class : {className}
                      </Badge>
                    )}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 items-center">
                    <div className="text-center text-sm font-medium">
                      {totalPrice > 0 && (
                        <Badge
                          variant="outline"
                          className="capitalize bg-custom-slider-bg-gray"
                        >
                          Total: {formatCurrency(totalPrice)}
                        </Badge>
                      )}
                      {totalSaveAmount > 0 && (
                        <Badge
                          variant="outline"
                          className="capitalize bg-custom-slider-bg-gray"
                        >
                          save {formatCurrency(totalSaveAmount)}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center space-x-2 select-none">
                      <Checkbox
                        id="selected"
                        checked={isAllProductSelect}
                        onClick={(e) => {
                          setAllProductSelect(!isAllProductSelect);
                          if (!isAllProductSelect === true) {
                            setSchoolSet([
                              ...academicProducts,
                              ...stationaryProducts,
                            ]);
                          } else {
                            removeSchoolSet();
                          }
                        }}
                        className="text-custom-text-blue"
                      />
                      <label
                        htmlFor="selected"
                        className="text-sm font-medium cursor-pointer"
                      >
                        {isAllProductSelect ? "Unselect All" : "Select All"}
                      </label>
                    </div>
                    <div className=" flex flex-col items-center">
                      <Button
                        className="bg-custom-blue h-full  text-sm text-white rounded-none flex items-center gap-2"
                        onClick={() => {
                          if (schoolSet?.length === 0) {
                            toast({
                              title: "Add to cart",
                              description:
                                "Select at least one item to add to your cart.",
                            });
                          } else {
                            toast({
                              title: "Added to cart",
                              description: `Academic set was added to the shopping cart.`,
                            });
                            setAddToCart(schoolSet);
                            window.dataLayer = window.dataLayer || [];
                            window.dataLayer.push({ ecommerce: null });
                      
                            window.dataLayer.push({
                              event: "add_to_cart",
                              ecommerce: {
                                currency: "BDT",
                                value: totalPrice,
                                items: schoolSet
                              },
                            });
                          }
                        }}
                      >
                        <ShoppingCart size={16} /> Add All to cart
                      </Button>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-3 sm:px-6 ">
                <div>
                  <div className="flex gap-3">
                    <h3 className=" font-semibold text-lg pb-5">
                      Academic Books
                    </h3>
                    <span className="text-[#6e737c]">
                      ( Total : {totalAcademic} )
                    </span>
                  </div>
                  {academicProducts?.length === 0 ? (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-xl font-medium">
                          <LibraryBig strokeWidth={1} size={45} />
                          <p>No Products Found</p>
                        </CardTitle>
                        <CardDescription className="text-custom-black ">
                          We couldn&apos;t find any products matching your
                          criteria. Please adjust your search or reset filters.
                          For assistance, contact our support. Thank you.
                        </CardDescription>
                      </CardHeader>
                    </Card>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
                      {academicProducts?.map((book, i) => (
                        <div
                          className="w-full  relative overflow-hidden"
                          key={i}
                        >
                          <AcademicProductCard product={book} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            {/* stationary card */}
            <Card className="flex flex-col gap-4 w-full bg-none">
              <CardHeader className="pb-2">
                <div className="flex gap-3">
                  <h3 className="font-semibold text-lg">Stationary</h3>{" "}
                  <span className="text-[#6e737c]">
                    ( Total : {totalStationary} )
                  </span>
                </div>
              </CardHeader>
              <CardContent className="px-3 sm:px-6">
                {stationaryProducts?.length === 0 ? (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-xl font-medium">
                        <LibraryBig strokeWidth={1} size={45} />
                        <p>No Products Found</p>
                      </CardTitle>
                      <CardDescription className="text-custom-black ">
                        We couldn&apos;t find any products matching your
                        criteria. Please adjust your search or reset filters.
                        For assistance, contact our support. Thank you.
                      </CardDescription>
                    </CardHeader>
                  </Card>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3  lg:grid-cols-5 gap-3 sm:gap-4">
                    {stationaryProducts?.map((book, i) => (
                      <div key={i} className="w-full  relative overflow-hidden">
                        <AcademicProductCard product={book} />
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Products;
